import { MediaHandler } from 'matrix-js-sdk/lib/webrtc/mediaHandler';
import {
  buildCallAudioConstraints,
  buildCallVideoQualityConstraints,
} from '../../services/call/callMediaConstraints';

const PATCH_FLAG = '__juicyhouseMatrixVideoQualityPatched';

/**
 * matrix-js-sdk MediaHandler defaults to 640x360 (WebKit uses width/height `exact`),
 * which caps camera quality and looks poor when scaled to full-screen video calls.
 *
 * Overlay resolution/framerate only. The SDK's 3rd argument is a boolean
 * (`exact` vs `ideal` deviceId) — never pass it to `buildCallVideoConstraints`,
 * which would set `deviceId: { exact: true }` and then fall back to the front
 * camera (`facingMode: user`), so flip always stayed on the selfie cam.
 */
export function patchMatrixMediaHandlerVideoQuality() {
  if (typeof MediaHandler === 'undefined' || !MediaHandler?.prototype) return;
  if (MediaHandler.prototype[PATCH_FLAG]) return;

  const original = MediaHandler.prototype.getUserMediaContraints;
  if (typeof original !== 'function') return;

  MediaHandler.prototype[PATCH_FLAG] = true;
  MediaHandler.prototype.getUserMediaContraints = function getUserMediaContraintsPatched(
    audio,
    video,
    useExactDeviceId,
  ) {
    const constraints = original.call(this, audio, video, useExactDeviceId);
    if (!constraints) return constraints;

    if (audio) {
      if (constraints.audio === true) {
        constraints.audio = buildCallAudioConstraints();
      } else if (constraints.audio && typeof constraints.audio === 'object') {
        constraints.audio = buildCallAudioConstraints(constraints.audio);
      }
    }

    if (!video || typeof constraints.video !== 'object' || constraints.video === false) {
      return constraints;
    }

    const quality = buildCallVideoQualityConstraints();
    const sdkVideo = constraints.video;
    constraints.video = {
      ...sdkVideo,
      width: quality.width,
      height: quality.height,
      frameRate: quality.frameRate,
    };
    if (sdkVideo.deviceId) {
      constraints.video.deviceId = sdkVideo.deviceId;
      delete constraints.video.facingMode;
    } else if (sdkVideo.facingMode) {
      constraints.video.facingMode = sdkVideo.facingMode;
    } else if (!constraints.video.facingMode) {
      constraints.video.facingMode = { ideal: 'user' };
    }
    return constraints;
  };
}
